import { useFlags, useFlagsmith } from 'flagsmith/react';
import type { IFlagsmithFeature } from 'flagsmith/types';
import type { TupleToUnion } from 'type-fest';
import {
  useFlagsmithIdentityLoading,
  useFlagsmithForceLoad,
} from '../providers/FeatureFlagProvider';

export const SUPPORTED_FEATURE_FLAG_KEYS = [
  'flag_addon_users_billing',
  'flag_ai_content_type_v2',
  'flag_alternate_audio_localizations',
  'flag_anonymous_edit',
  'flag_app_redesign_2024',
  'flag_brandfetch_wtw',
  'flag_caption_customizations',
  'flag_customize_analytics',
  'flag_edit_recordings_experiment',
  'flag_email_add_video_experiment',
  'flag_email_integration_detail_pages',
  'flag_hls_in_editor',
  'flag_in_progress_integrations',
  'flag_editor_filler_words',
  'flag_enable_editor_react_scan_monitoring',
  'flag_enable_live_react_scan_monitoring',
  'flag_enable_media_page_react_scan_monitoring',
  'flag_enable_folder_page_react_scan_monitoring',
  'flag_enable_record_react_scan_monitoring',
  'flag_extended_audio_descriptions',
  'flag_folder_page_info_panel',
  'flag_folder_filters',
  'flag_folders_move_and_copy',
  'flag_group_permissions',
  'flag_group_record_spotlight_layout',
  'flag_in_app_integrations_marketplace',
  'flag_join_your_org_settings',
  'flag_live_attendee_list',
  'flag_live_chat_notifications',
  'flag_live_disallow_invalid_emails',
  'flag_live_embed_audience',
  'flag_live_emoji_reactions',
  'flag_live_event_account_overview',
  'flag_live_ics_attachment',
  'flag_live_pip',
  'flag_live_realtime_audience',
  'flag_live_registration_page_layout',
  'flag_live_restartable',
  'flag_live_spotlight_layout',
  'flag_live_studio_joinkit',
  'flag_live_update_default_thumbnail_emails',
  'flag_live_virtual_message_list',
  'flag_live_wistia_player_conversion',
  'flag_llm_optimized_embed',
  'flag_map_integration_detail_pages',
  'flag_media_analytics_takeover_page',
  'flag_media_suggestions',
  'flag_network_connection_indicator',
  'flag_new_color_picker',
  'flag_no_plus_plan_experiment',
  'flag_on_demand_live_analytics',
  'flag_open_search',
  'flag_pricing_trials_navigational_change',
  'flag_quick_search_v2',
  'flag_recent_searches',
  'flag_record_local_file_recovery',
  'flag_record_screenshare_on_preflight',
  'flag_record_use_livekit',
  'flag_record_mp4_conversion',
  'flag_remotion_video_bumper',
  'flag_revamped_search',
  'flag_search',
  'flag_hackathon_shoppable_video',
  'flag_show_conversion_analytics',
  'flag_show_traffic_analytics',
  'flag_slack_notifications',
  'flag_smart_form_v1_identities',
  'flag_speaker_management',
  'flag_transcript_search',
  'flag_trial_prompt_order_experiment',
  'flag_upcoming_invoice_account_settings',
  'flag_use_case_nudges',
  'flag_use_onboarding',
  'flag_verify_email',
  'flag_webinar_spotlight',
  'flag_wtw_add_video_experiment',
  'flag_use_vma_stage_state',
  'flag_editor_auto_edit',
  'flag_editor_sessions_v2',
  'flag_editor_silence_detection',
  'flag_editor_subject_detection',
  'flag_live_overview_page_redesign',
  'flag_transcript_diarization',
  'flag_transcript_webhooks',
  'flag_trial_prompt_experiment',
  'flag_editor_audio_social_clips',
  'billing_settings_redesign',
  'flag_editor_rescript',
  'flag_wtw_wistiaui',
  'flag_folder_sorting_v2',
  'flag_enable_webinar_wizard',
] as const;

export type SupportedFeatureFlags = TupleToUnion<typeof SUPPORTED_FEATURE_FLAG_KEYS>;

export type UseLoadFeatureFlagsReturn<
  Flag extends SupportedFeatureFlags = Partial<SupportedFeatureFlags>,
> = {
  readonly loading: boolean;

  readonly flags: Record<Flag, IFlagsmithFeature>;
};

/**
 * V2 hook differs from v1 by checking if a flag has been loaded. Flags that are not loaded are returned as disabled by flagsmith by default.
 * Testing example can be seen here frontend/singlePageApp/hooks/useLoadFeatureFlagsV2.test.jsx
 */
export const useLoadFeatureFlags = <
  Flag extends SupportedFeatureFlags = Partial<SupportedFeatureFlags>,
  Trait extends string = '',
>(
  flags: Flag[],
  traits: Trait[] | undefined = undefined,
): UseLoadFeatureFlagsReturn<Flag> => {
  /**
   * If a user has never had the flag explicitly toggled on/off
   * OR flagsmith goes down, we need to force the loading state
   * after a few seconds to avoid the UI getting stuck in an infinite
   * loader.
   */
  const forceLoad = useFlagsmithForceLoad();
  const flagsmith = useFlagsmith();
  const result = useFlags(flags, traits);
  const flagsmithStateFlags = flagsmith.getState().flags;
  const flagsmithIdentityLoading = useFlagsmithIdentityLoading();

  const hasAllFlags =
    forceLoad ||
    flags.reduce((acc, flag) => {
      if (flagsmithStateFlags === undefined) {
        return false;
      }
      /**
       * Disabling because the TS compiler doesn't realize that we can provide a string that's not in the flags object.
       * Possible typing problem in Flagsmith
       */
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      return acc && !!flagsmithStateFlags[flag];
    }, true);

  return {
    loading: flagsmithIdentityLoading || !hasAllFlags,
    flags: result as Record<Flag, IFlagsmithFeature>,
  };
};
